import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, Fullscreen } from '@mui/icons-material';
import { LD } from './tokens';

const GAP = 6;
// Slides the browser fetches up front; the rest load as they near the view.
const EAGER_SLIDES = 3;

export interface PhotoCarouselHandle {
  scrollToIndex: (index: number) => void;
}

interface PhotoCarouselProps {
  photos: string[];
  /** Phone: full-bleed slides with a peek of the next. Desktop: a strip of 440px tiles. */
  variant: 'phone' | 'desktop';
  index: number;
  onIndexChange: (index: number) => void;
  onOpenViewer: (index: number) => void;
  /** Controls drawn over the photo (share, close). */
  overlay?: React.ReactNode;
}

const pillSx = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: 'normal',
  px: '11px',
  py: '5px',
  borderRadius: '999px',
  zIndex: 3,
} as const;

/**
 * The listing's photos as a native swipe strip (CSS scroll-snap). Tapping a
 * photo opens the full-screen viewer at that photo.
 */
const PhotoCarousel = forwardRef<PhotoCarouselHandle, PhotoCarouselProps>(
  ({ photos, variant, index, onIndexChange, onOpenViewer, overlay }, ref) => {
    const isPhone = variant === 'phone';
    const height = isPhone ? 300 : 330;
    const trackRef = useRef<HTMLDivElement | null>(null);
    // Photos whose bytes have arrived; a slide shows a spinner until then.
    const [loaded, setLoaded] = useState<Set<string>>(() => new Set());
    const [atEnd, setAtEnd] = useState(photos.length <= 1);

    const slideStep = useCallback((): number => {
      const first = trackRef.current?.firstElementChild as HTMLElement | null;
      return first ? first.offsetWidth + GAP : 1;
    }, []);

    const scrollToIndex = useCallback(
      (target: number, smooth = false) => {
        const track = trackRef.current;
        if (!track) return;
        track.scrollTo({
          left: target * slideStep(),
          behavior: smooth ? 'smooth' : 'auto',
        });
      },
      [slideStep]
    );

    useImperativeHandle(ref, () => ({ scrollToIndex }), [scrollToIndex]);

    const handleScroll = () => {
      const track = trackRef.current;
      if (!track) return;
      const next = Math.min(
        photos.length - 1,
        Math.max(0, Math.round(track.scrollLeft / slideStep()))
      );
      if (next !== index) onIndexChange(next);
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
    };

    useEffect(() => {
      const track = trackRef.current;
      if (track) {
        setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
      }
    }, [photos.length]);

    const markLoaded = (src: string) =>
      setLoaded((prev) => {
        if (prev.has(src)) return prev;
        const next = new Set(prev);
        next.add(src);
        return next;
      });

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollToIndex(Math.min(photos.length - 1, index + 1), true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollToIndex(Math.max(0, index - 1), true);
      }
    };

    const single = photos.length === 1;
    let slideWidth = '440px';
    if (isPhone) slideWidth = single ? '100%' : 'calc(100% - 30px)';

    return (
      <Box
        role="region"
        aria-roledescription="carousel"
        aria-label="Property photo gallery"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'relative',
          height,
          flexShrink: 0,
          bgcolor: '#111',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <Box
          ref={trackRef}
          data-testid="photo-carousel-track"
          onScroll={handleScroll}
          sx={{
            display: 'flex',
            gap: `${GAP}px`,
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            overscrollBehaviorX: 'contain',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {photos.map((src, i) => (
            <Box
              key={`${src}-${i}`}
              onClick={() => onOpenViewer(i)}
              sx={{
                position: 'relative',
                flex: `0 0 ${slideWidth}`,
                height: '100%',
                scrollSnapAlign: 'start',
                cursor: 'pointer',
                bgcolor: '#1f2937',
              }}
            >
              <img
                src={src}
                loading={i < EAGER_SLIDES ? 'eager' : 'lazy'}
                decoding="async"
                draggable={false}
                onLoad={() => markLoaded(src)}
                onError={() => markLoaded(src)}
                alt={`Property photo ${i + 1} of ${photos.length}`}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {!loaded.has(src) && (
                <Box
                  role="status"
                  aria-label="Loading photo"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={32} sx={{ color: 'white' }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {overlay}

        {isPhone && photos.length > 1 && (
          <>
            {index === 0 && (
              <Box
                data-testid="photo-swipe-hint"
                sx={{
                  ...pillSx,
                  left: 12,
                  bottom: 12,
                  bgcolor: LD.pillLight,
                  color: LD.ink,
                  pointerEvents: 'none',
                }}
              >
                <ChevronLeft sx={{ fontSize: 16 }} />
                Swipe for {photos.length - 1} more
              </Box>
            )}
            <Box
              data-testid="photo-counter"
              aria-live="polite"
              sx={{
                ...pillSx,
                right: 42,
                bottom: 12,
                bgcolor: LD.pill,
                color: 'white',
                pointerEvents: 'none',
              }}
            >
              {index + 1} / {photos.length}
            </Box>
          </>
        )}

        {!isPhone && photos.length > 1 && (
          <>
            {index > 0 && (
              <IconButton
                aria-label="Previous photo"
                onClick={() => scrollToIndex(index - 1, true)}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: (height - 44) / 2,
                  width: 44,
                  height: 44,
                  bgcolor: 'white',
                  color: LD.ink,
                  zIndex: 3,
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
              >
                <ChevronLeft sx={{ fontSize: 26 }} />
              </IconButton>
            )}
            {!atEnd && (
              <IconButton
                aria-label="Next photo"
                onClick={() => scrollToIndex(index + 1, true)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: (height - 44) / 2,
                  width: 44,
                  height: 44,
                  bgcolor: 'white',
                  color: LD.ink,
                  zIndex: 3,
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
              >
                <ChevronRight sx={{ fontSize: 26 }} />
              </IconButton>
            )}
          </>
        )}
        {!isPhone && photos.length > 0 && (
          <Box
            component="button"
            type="button"
            data-testid="photo-counter"
            onClick={() => onOpenViewer(index)}
            sx={{
              ...pillSx,
              right: 16,
              bottom: 14,
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              bgcolor: LD.pill,
              color: 'white',
            }}
          >
            <Fullscreen sx={{ fontSize: 15 }} />
            {index + 1} / {photos.length} · View all
          </Box>
        )}
      </Box>
    );
  }
);

PhotoCarousel.displayName = 'PhotoCarousel';

export default PhotoCarousel;
